import { useEffect, useContext, useMemo } from 'react'
import {
  Button,
  ExpandableSection,
  Grid,
  Layout,
  Input,
} from '@enterprise-ui/canvas-ui-react'
import { DatePicker } from '@enterprise-ui/canvas-ui-react-datepicker'
import {
  COST_CHANGE_INSIGHTS_ITEM_OPTIONS,
  COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS,
  COST_CHANGE_INSIGHTS_LOCATION_OPTIONS,
  COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS,
  COST_CHANGE_INSIGHTS_MISC_OPTIONS,
} from '../../globalConfig/constants'
import {
  getClassOptions,
  getCustomLocationGroupOptions,
} from '../../services/requests'
import { AutocompleteFilter } from '../../globalComponents/AutocompleteFilter'
import { TextFilter } from '../../globalComponents/TextFilter'
import { UnrecognizedInputsModal } from '../../globalComponents/UnrecognizedInputsModal'
import { CostChangeInsightsPageContext } from './pageState'
import { CostChangeInsightsGlobalContext } from '../../globalState/CostChangeInsights'
import { VendorContext } from '../../globalState/Vendor'
import {
  getSelectionsAndErrorsFromValueStates,
  isItemAddCriteriaButtonDisabled,
  constructViewByLevelAutocompleteOptions,
  isResetSelectionsButtonDisabled,
  isSearchButtonDisabled,
  isViewByValueInputDisabled,
  stripItemHubPath,
} from './helperFunctions'
import isEqual from 'lodash/isEqual'
import usePrevious from '../../globalHooks/usePrevious'

export const CostChangeInsightsFilters = ({
  onSearchClick,
  highlightedFieldKeyNames,
}) => {
  const [pageState, pageDispatch] = useContext(CostChangeInsightsPageContext)
  const [globalState, globalDispatch] = useContext(
    CostChangeInsightsGlobalContext,
  )
  const [vendorState] = useContext(VendorContext)
  const viewByLevelAutocompleteOptions = useMemo(
    () => constructViewByLevelAutocompleteOptions(),
    [],
  )
  const pathname = stripItemHubPath(window.location.pathname)

  const {
    valueStates,
    errorStates,
    classOptions,
    customCLGOptions,
    isErrorModalVisible,
    filterExpandableStates,
  } = pageState
  const departmentValue =
    valueStates[COST_CHANGE_INSIGHTS_ITEM_OPTIONS.DEPARTMENTS.variableName]
  const vendorIdValue = [vendorState.selectedVendor]
  const viewByLevelValue =
    valueStates[
      COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
        .variableName
    ]
  const previousViewByLevelValue = usePrevious(viewByLevelValue)

  useEffect(() => {
    if (departmentValue.length === 1) {
      getClassOptions(departmentValue[0].value)
        .then((response) => {
          if (response?.status === 200) {
            pageDispatch({ type: 'update_class_options', value: response.data })
          }
        })
        .catch(() => {})
    } else {
      pageDispatch({ type: 'reset_class_options' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentValue])

  useEffect(() => {
    if (
      previousViewByLevelValue &&
      previousViewByLevelValue !== viewByLevelValue
    ) {
      pageDispatch({ type: 'update_view_by_values_value_state', value: [] })
      globalDispatch({
        type: 'update_cost_location_group_values',
        value: [],
        pathname: pathname,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewByLevelValue])

  // This useEffect handles fetching and removing custom CLG options when new
  // vendors are selected or removed
  useEffect(() => {
    vendorIdValue.forEach((vendor) => {
      if (!pageState.customCLGOptions[vendor]) {
        getCustomLocationGroupOptions(vendor)
          .then((response) => {
            if (response?.status === 200) {
              pageDispatch({
                type: 'add_custom_clg_options',
                value: { [vendor]: response.data },
              })
            }
          })
          .catch(() => {})
      }
    })

    Object.keys(pageState.customCLGOptions).forEach((vendorId) => {
      if (!vendorIdValue.find((vendor) => vendor === vendorId)) {
        if (
          valueStates[
            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_TYPE
              .variableName
          ] === COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS.CUSTOM.variableName
        ) {
          if (vendorIdValue.length) {
            const viewByValuesWithoutAnyFromTheGivenVendor = valueStates[
              COST_CHANGE_INSIGHTS_LOCATION_OPTIONS.COST_LOCATION_GROUP_VALUES
                .variableName
            ].filter((viewByValue) => {
              return !pageState.customCLGOptions[vendorId].some(
                (customCLGOption) => isEqual(customCLGOption, viewByValue),
              )
            })
            pageDispatch({
              type: 'update_view_by_values_value_state',
              value: viewByValuesWithoutAnyFromTheGivenVendor,
            })
            globalDispatch({
              type: 'update_cost_location_group_values',
              value: viewByValuesWithoutAnyFromTheGivenVendor,
              pathname: pathname,
            })
          } else {
            pageDispatch({
              type: 'update_view_by_values_value_state',
              value: [],
            })
            globalDispatch({
              type: 'update_cost_location_group_values',
              value: [],
              pathname: pathname,
            })
          }
        }
        pageDispatch({
          type: 'remove_custom_clg_options',
          value: vendorId,
        })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorState.selectedVendor])

  const handleAddCriteriaClick = (makeSearchRequestAfterAdding) => {
    const selectionsAndErrors =
      getSelectionsAndErrorsFromValueStates(valueStates)
    pageDispatch({
      type: 'update_error_states',
      value: selectionsAndErrors.errors,
    })
    pageDispatch({ type: 'clear_item_value_states' })
    globalDispatch({
      type: 'update_criteria_for_multiple_fields',
      value: selectionsAndErrors.selections,
      pathname: pathname,
    })
    if (
      makeSearchRequestAfterAdding &&
      !Object.keys(selectionsAndErrors.errors).length
    ) {
      pageDispatch({ type: 'initiate_search' })
    }
  }

  const handleSearchClick = () => {
    if (isItemAddCriteriaButtonDisabled(valueStates)) {
      onSearchClick()
    } else {
      handleAddCriteriaClick(true)
    }
  }

  return (
    <>
      <Layout.Body
        className="hesp-costSearch-layout"
        data-testid="cost-change-insights-body"
      >
        <Grid.Container className="hc-pa-normal">
          <Grid.Item className="hesp-general-fullwidth hc-pa-none">
            <div className="hesp-general-flex hesp-general-justifyEnd">
              <Button
                className="hesp-general-ghostButton hc-mb-xs"
                data-testid="collapse-all-button"
                type="ghost"
                onClick={() => {
                  pageDispatch({ type: 'update_all_expandable_states' })
                }}
              >
                {Object.keys(filterExpandableStates).some(
                  (key) => filterExpandableStates[key],
                )
                  ? 'COLLAPSE ALL'
                  : 'EXPAND ALL'}
              </Button>
            </div>
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              expanded={filterExpandableStates.ITEM}
              onExpand={() => {
                pageDispatch({
                  type: 'flip_expandable_state',
                  value: 'ITEM',
                })
              }}
            >
              <p className="hc-fs-md">
                <strong>Item Filters</strong>
              </p>
              <ExpandableSection.Content>
                <div
                  style={{ columnGap: '24px' }}
                  className="hesp-general-flex hesp-general-flex-wrap hesp-general-alignItemsCenter hc-pl-lg hc-pt-xs"
                >
                  {Object.values(COST_CHANGE_INSIGHTS_ITEM_OPTIONS).map(
                    (field) => (
                      <div
                        key={`${field.variableName}_container`}
                        className="hesp-general-scContainer"
                      >
                        {field?.filterType === 'AUTOCOMPLETE' ? (
                          <AutocompleteFilter
                            id={`${field.variableName}_autocomplete`}
                            label={field.label}
                            fieldName={
                              field?.costChangeInsightsFilterDetails
                                ?.autocompleteRequestName
                            }
                            optionsRequest={
                              field?.keyName === 'DEPARTMENT_CLASS'
                                ? classOptions
                                : field?.costChangeInsightsFilterDetails
                                    ?.autocompleteRequestFunc
                            }
                            sortOptions={
                              field?.costChangeInsightsFilterDetails?.sortFunc
                            }
                            selectedOptions={(input) => {
                              pageDispatch({
                                type: 'update_value_states_by_field',
                                field: field,
                                value: input,
                              })
                            }}
                            requestType={
                              field?.costChangeInsightsFilterDetails
                                ?.requestType
                            }
                            data-testid={`${field.variableName}_autocomplete`}
                            multiselect
                            optionHeight="expanded"
                            value={valueStates[field.variableName]}
                            disabled={
                              field?.keyName === 'DEPARTMENT_CLASS'
                                ? valueStates[
                                    COST_CHANGE_INSIGHTS_ITEM_OPTIONS
                                      .DEPARTMENTS.variableName
                                  ].length !== 1
                                : null
                            }
                            highlight={highlightedFieldKeyNames?.includes(
                              field?.keyName,
                            )}
                          />
                        ) : (
                          <TextFilter
                            id={`${field.variableName}_text_filter`}
                            label={field.label}
                            data-testid={`${field.variableName}_text_filter`}
                            onChange={(input) => {
                              pageDispatch({
                                type: 'update_value_states_by_field',
                                field: field,
                                value: input?.target?.value,
                              })
                            }}
                            value={valueStates?.[field.variableName]}
                            highlight={highlightedFieldKeyNames?.includes(
                              field?.keyName,
                            )}
                          />
                        )}
                      </div>
                    ),
                  )}
                  <div
                    style={{ flexGrow: 1, height: '50px' }}
                    className="hesp-general-flex hesp-general-justifyEnd hesp-general-alignItemsCenter hc-pr-xs"
                  >
                    <Button
                      type="secondary"
                      onClick={(e) => handleAddCriteriaClick()}
                      disabled={isItemAddCriteriaButtonDisabled(valueStates)}
                      data-testid="add_criteria_button"
                    >
                      ADD CRITERIA
                    </Button>
                  </div>
                </div>
              </ExpandableSection.Content>
            </ExpandableSection>
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              expanded={filterExpandableStates.LOCATION}
              onExpand={() => {
                pageDispatch({
                  type: 'flip_expandable_state',
                  value: 'LOCATION',
                })
              }}
            >
              <p className="hc-fs-md">
                <strong>Location Level Filters</strong>
              </p>
              <ExpandableSection.Content>
                <div
                  style={{ columnGap: '24px', alignItems: 'start' }}
                  className="hesp-general-flex hesp-general-flex-wrap hc-pl-lg hc-pt-xs"
                >
                  <div
                    key={`view_by_level_container`}
                    className="hesp-general-scContainer"
                  >
                    <Input.Label htmlFor="view_by_level_autocomplete">
                      View By Level
                    </Input.Label>
                    <Input.Select
                      id="view_by_level_autocomplete"
                      label="View By Level"
                      options={viewByLevelAutocompleteOptions}
                      onUpdate={(id, value) => {
                        pageDispatch({
                          type: 'update_view_by_level_value_state',
                          value: value,
                        })
                        globalDispatch({
                          type: 'update_cost_location_group_type',
                          value: value,
                          pathname: pathname,
                        })
                      }}
                      data-testid="view_by_level_select"
                      value={
                        valueStates?.[
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_TYPE.variableName
                        ]
                      }
                      highlight={highlightedFieldKeyNames?.includes(
                        COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                          .COST_LOCATION_GROUP_TYPE?.keyName,
                      )}
                    />
                  </div>
                  <div style={{ width: 'calc(77% - 24px)', minWidth: '175px' }}>
                    <AutocompleteFilter
                      id={`${
                        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                          valueStates[
                            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                              .COST_LOCATION_GROUP_TYPE.variableName
                          ]
                        ]?.variableName
                      }_autocomplete`}
                      label={
                        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                          valueStates[
                            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                              .COST_LOCATION_GROUP_TYPE.variableName
                          ]
                        ]?.filterType !== 'AUTOCOMPLETE'
                          ? 'View By Value'
                          : COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                              valueStates[
                                COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                                  .COST_LOCATION_GROUP_TYPE.variableName
                              ]
                            ]?.label
                      }
                      fieldName={
                        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                          valueStates[
                            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                              .COST_LOCATION_GROUP_TYPE.variableName
                          ]
                        ]?.costChangeInsightsFilterDetails
                          ?.autocompleteRequestName
                      }
                      optionsRequest={
                        valueStates[
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_TYPE.variableName
                        ] === 'CUSTOM'
                          ? Object.values(customCLGOptions).flat()
                          : COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                              valueStates[
                                COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                                  .COST_LOCATION_GROUP_TYPE.variableName
                              ]
                            ]?.costChangeInsightsFilterDetails
                              ?.autocompleteRequestFunc
                      }
                      selectedOptions={(input) => {
                        pageDispatch({
                          type: 'update_view_by_values_value_state',
                          value: input,
                        })
                        globalDispatch({
                          type: 'update_cost_location_group_values',
                          value: input,
                          pathname: pathname,
                        })
                      }}
                      requestType={
                        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                          valueStates[
                            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                              .COST_LOCATION_GROUP_TYPE.variableName
                          ]
                        ]?.costChangeInsightsFilterDetails?.requestType
                      }
                      data-testid={`${
                        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS[
                          valueStates[
                            COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                              .COST_LOCATION_GROUP_TYPE.variableName
                          ]
                        ]?.variableName
                      }_autocomplete`}
                      multiselect
                      required={!isViewByValueInputDisabled(valueStates)}
                      value={
                        valueStates?.[
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_VALUES.variableName
                        ]
                      }
                      disabled={isViewByValueInputDisabled(valueStates)}
                      hintText={
                        valueStates[
                          COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                            .COST_LOCATION_GROUP_TYPE.variableName
                        ] ===
                        COST_CHANGE_INSIGHTS_VIEW_BY_OPTIONS.CUSTOM.variableName
                          ? 'Custom Location Group options correspond with your currently represented vendor. Make sure you have the correct vendor selected.'
                          : null
                      }
                      highlight={highlightedFieldKeyNames?.includes(
                        COST_CHANGE_INSIGHTS_LOCATION_OPTIONS
                          .COST_LOCATION_GROUP_VALUES?.keyName,
                      )}
                    />
                  </div>
                </div>
              </ExpandableSection.Content>
            </ExpandableSection>
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              expanded={filterExpandableStates.TIMEFRAME}
              onExpand={() => {
                pageDispatch({
                  type: 'flip_expandable_state',
                  value: 'TIMEFRAME',
                })
              }}
            >
              <p className="hc-fs-md">
                <strong>Timeframe Filters</strong>
              </p>
              <ExpandableSection.Content>
                <div
                  style={{ columnGap: '24px' }}
                  className="hesp-general-flex hesp-general-flex-wrap hesp-general-alignItemsCenter hc-pl-lg hc-pt-xs"
                >
                  {Object.values(COST_CHANGE_INSIGHTS_TIMEFRAME_OPTIONS).map(
                    (field) => (
                      <div
                        key={`${field.variableName}_container`}
                        className="hesp-general-scContainer"
                      >
                        {field?.filterType === 'DATE_RANGE' ? (
                          <DatePicker
                            className={
                              field?.keyName === 'DECIDED_DATE_BETWEEN'
                                ? 'hesp-general-smaller-label'
                                : null
                            }
                            id={`${field.variableName}_date_range_picker`}
                            label={field.label}
                            data-testid={`${field.variableName}_date_range_picker`}
                            type="gregorian-range"
                            noValidate
                            onUpdate={(id, input) => {
                              if (input?.startDate && input?.endDate) {
                                pageDispatch({
                                  type: 'update_value_states_by_field',
                                  field: field,
                                  value: input,
                                })
                                globalDispatch({
                                  type: 'update_criteria_by_field',
                                  field: field,
                                  value: input,
                                  pathname: pathname,
                                })
                              } else {
                                pageDispatch({
                                  type: 'update_value_states_by_field',
                                  field: field,
                                  value: {},
                                })
                                globalDispatch({
                                  type: 'update_criteria_by_field',
                                  field: field,
                                  value: null,
                                  pathname: pathname,
                                })
                              }
                            }}
                            value={valueStates[field.variableName]}
                            highlight={highlightedFieldKeyNames?.includes(
                              field?.keyName,
                            )}
                          />
                        ) : null}
                      </div>
                    ),
                  )}
                </div>
              </ExpandableSection.Content>
            </ExpandableSection>
            <ExpandableSection
              className="hesp-general-expandable"
              padding="none"
              preserveDom
              expanded={filterExpandableStates.MISC}
              onExpand={() => {
                pageDispatch({
                  type: 'flip_expandable_state',
                  value: 'MISC',
                })
              }}
            >
              <p className="hc-fs-md">
                <strong>Miscellaneous Filters</strong>
              </p>
              <ExpandableSection.Content>
                <div
                  style={{ columnGap: '24px' }}
                  className="hesp-general-flex hesp-general-flex-wrap hesp-general-alignItemsCenter hc-pl-lg hc-pt-xs"
                >
                  {Object.values(COST_CHANGE_INSIGHTS_MISC_OPTIONS).map(
                    (field) => (
                      <div
                        key={`${field.variableName}_container`}
                        className="hesp-general-scContainer"
                      >
                        {field?.filterType === 'AUTOCOMPLETE' ? (
                          <AutocompleteFilter
                            id={`${field.variableName}_autocomplete`}
                            label={field.label}
                            fieldName={
                              field?.costChangeInsightsFilterDetails
                                ?.autocompleteRequestName
                            }
                            optionsRequest={
                              field?.costChangeInsightsFilterDetails
                                ?.autocompleteRequestFunc
                            }
                            sortOptions={
                              field?.costChangeInsightsFilterDetails?.sortFunc
                            }
                            selectedOptions={(input) => {
                              pageDispatch({
                                type: 'update_value_states_by_field',
                                field: field,
                                value: input,
                              })
                              globalDispatch({
                                type: 'update_criteria_by_field',
                                field: field,
                                value: input.map((obj) => obj.value),
                                pathname: pathname,
                              })
                            }}
                            requestType={
                              field?.costChangeInsightsFilterDetails
                                ?.requestType
                            }
                            data-testid={`${field.variableName}_autocomplete`}
                            multiselect
                            optionHeight="expanded"
                            value={valueStates[field.variableName]}
                            highlight={highlightedFieldKeyNames?.includes(
                              field?.keyName,
                            )}
                          />
                        ) : (
                          <TextFilter
                            id={`${field.variableName}_text_filter`}
                            label={field.label}
                            data-testid={`${field.variableName}_text_filter`}
                            onChange={(input) => {
                              pageDispatch({
                                type: 'update_value_states_by_field',
                                field: field,
                                value: input?.target?.value || null,
                              })
                              globalDispatch({
                                type: 'update_criteria_by_field',
                                field: field,
                                value:
                                  input?.target?.value.trim().toUpperCase() ||
                                  null,
                                pathname: pathname,
                              })
                            }}
                            value={valueStates?.[field.variableName]}
                            highlight={highlightedFieldKeyNames?.includes(
                              field?.keyName,
                            )}
                          />
                        )}
                      </div>
                    ),
                  )}
                </div>
              </ExpandableSection.Content>
            </ExpandableSection>
            <div className="hesp-general-flex hesp-general-justifyEnd hc-pt-normal">
              <Button
                className="hc-mr-normal"
                type="secondary"
                onClick={() => {
                  pageDispatch({
                    type: 'reset_value_states_to_default',
                    pathname: pathname,
                  })
                  pageDispatch({
                    type: 'set_cost_change_items',
                    value: null,
                  })
                  pageDispatch({ type: 'reset_current_page' })
                  globalDispatch({
                    type: 'reset_criteria_to_default',
                    pathname: pathname,
                  })
                }}
                disabled={isResetSelectionsButtonDisabled(
                  globalState?.[pathname].searchCriteria,
                  pageState,
                  pathname,
                )}
                data-testid="reset_selections_button"
              >
                RESET SELECTIONS
              </Button>
              <Button
                type="primary"
                onClick={handleSearchClick}
                data-testid="search_button"
                disabled={isSearchButtonDisabled(valueStates)}
              >
                SEARCH
              </Button>
            </div>
          </Grid.Item>
        </Grid.Container>
        <UnrecognizedInputsModal
          isVisible={isErrorModalVisible}
          onRefuse={() => {
            pageDispatch({
              type: 'update_error_modal_visibility',
              value: false,
            })
          }}
          errorStates={errorStates}
          possibleFields={COST_CHANGE_INSIGHTS_ITEM_OPTIONS}
        />
      </Layout.Body>
    </>
  )
}
